import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/authservices';
import LoginForm from '../components/LoginForm';
import localStorageService from '../services/localStorageService';

interface LoginErrorState {
    Username?: string;
    Password?: string;
    General?: string;
}

function Login() {
    const [currentView, setCurrentView] = useState('login');
    const [isPending, setIsPending] = useState(false);
    const [issueDialogOpen, setIssueDialogOpen] = useState(false);

    const [loginUsername, setLoginUsername] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [loginError, setLoginError] = useState<LoginErrorState>({});
    const [showLoginPassword, setShowLoginPassword] = useState(false);

    const navigate = useNavigate();
    const authService = useMemo(() => new AuthService(), []);

    useEffect(() => {
        let isActive = true;

        const restoreSession = async () => {
            const isAuthenticated = await authService.VerifyAuth();

            if (isActive && isAuthenticated) {
                navigate('/dashboard', { replace: true });
            }
        };

        void restoreSession();

        return () => {
            isActive = false;
        };
    }, [authService, navigate]);

    const handleLogin = async () => {
        localStorageService.clearAll();
        setLoginError({});
        setIsPending(true);

        if (!loginUsername.trim() || !loginPassword.trim()) {
            setLoginError({ General: 'Username and Password are required.' });
            if (!loginUsername.trim()) setLoginError(prev => ({ ...prev, Username: 'Username required' }));
            if (!loginPassword.trim()) setLoginError(prev => ({ ...prev, Password: 'Password required' }));
            setIsPending(false);
            return;
        }

        const payload = new URLSearchParams();
        payload.append('username', loginUsername);
        payload.append('password', loginPassword);

        const response = await authService.UserLogin(payload);
        setIsPending(false);

        if (response && response.status === 200) {
            const userData = response.data;

            localStorageService.addUserInfo(userData);
            const sportCodes: string[] = Array.isArray(userData?.sports)
                ? userData.sports.map((sport: any) => sport?.code).filter(Boolean)
                : [];
            const sportCode = sportCodes.includes('MFB') ? 'MFB' : sportCodes[0] || null;

            if (sportCode) {
                localStorageService.setSelectedSport(sportCode);
            }

            navigate('/dashboard');
        } else {
            const errorMsg =
                response?.data?.message ||
                response?.data?.detail ||
                response?.message ||
                'Invalid username or password.';
            setLoginError({ General: errorMsg });
        }
    };

    const switchToForgotPasswordView = () => {
        setCurrentView('forgotPassword');
        setLoginError({});
    };

    const switchToLoginView = () => {
        setCurrentView('login');
    };

    return (
    <div className="w-full flex-1 flex flex-col justify-center items-center box-border bg-gradient-to-br from-gray-100 to-cyan-50 py-12 px-4">

    
                {currentView === 'login' && (
                    <>
                        <LoginForm
                            handleLogin={handleLogin}
                            username={loginUsername}
                            setUsername={setLoginUsername}
                            showPassword={showLoginPassword}
                            password={loginPassword}
                            setPassword={setLoginPassword}
                            setShowPassword={setShowLoginPassword}
                            error={loginError}
                            setError={setLoginError as any}
                            isPending={isPending}
                            switchToForgotPassword={switchToForgotPasswordView}
                        />
                        <div className="mt-4">
                            <button
                                className="text-sm text-blue-500 hover:text-blue-700 underline"
                                onClick={() => setIssueDialogOpen(true)}
                            >
                                Found an issue? Report it here
                            </button>
                        </div>
                    </>
                )}
                
               </div>
                  );
}

export default Login;
